import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';

import { BLACK } from '../../../theme/colorConstants';
import { SAVE_STATUS } from '../constants';

import { textFieldSx } from './AutoSaveTextField';
import FieldLabel from './FieldLabel';
import SaveStatusIndicator from './SaveStatusIndicator';

const rootSx = { display: 'flex', flexDirection: 'column', gap: '6px' };

const labelRowSx = { alignItems: 'center', display: 'flex', justifyContent: 'space-between' };

const placeholderSx = { color: BLACK[60] };

const AutoSaveSelectField = ({ id, label, value, options, placeholder, status, onSave }) => {
  const intl = useIntl();
  const hasValue = options.some((option) => option.value === value);

  return (
    <Box sx={rootSx}>
      <Box sx={labelRowSx}>
        <FieldLabel>
          <span id={`${id}-label`}>{label}</span>
        </FieldLabel>
        <SaveStatusIndicator status={status} />
      </Box>
      <TextField
        id={id}
        select
        size="small"
        fullWidth
        value={hasValue ? value : ''}
        error={status === SAVE_STATUS.ERROR}
        SelectProps={{
          labelId: `${id}-label`,
          displayEmpty: true,
          renderValue: (selected) => {
            const option = options.find((item) => item.value === selected);
            return option ? (
              intl.formatMessage({ id: option.labelId })
            ) : (
              <Box component="span" sx={placeholderSx}>
                {placeholder}
              </Box>
            );
          },
        }}
        onChange={(event) => {
          if (event.target.value !== value) onSave(event.target.value);
        }}
        sx={textFieldSx}
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {intl.formatMessage({ id: option.labelId })}
          </MenuItem>
        ))}
      </TextField>
    </Box>
  );
};

AutoSaveSelectField.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      labelId: PropTypes.string.isRequired,
    })
  ).isRequired,
  placeholder: PropTypes.string,
  status: PropTypes.oneOf(Object.values(SAVE_STATUS)),
  onSave: PropTypes.func.isRequired,
};

export default AutoSaveSelectField;
